import * as Sentry from '@sentry/react';

export type AppErrorSource = 'auth' | 'chat' | 'media' | 'network' | 'recording' | 'room' | 'screen-share' | 'unknown';

export type AppErrorSeverity = 'info' | 'warning' | 'error';

export type AppError = Omit<Error, 'code'> & {
  code?: number;
  cause?: unknown;
  source: AppErrorSource;
  severity: AppErrorSeverity;
  technicalMessage: string;
  userMessage: string;
  retryable: boolean;
};

export function isAppError(error: unknown): error is AppError {
  return typeof error === 'object' && error !== null && 'source' in error && 'technicalMessage' in error;
}

type NormalizeErrorOptions = {
  source?: AppErrorSource;
  severity?: AppErrorSeverity;
  technicalMessage?: string;
  userMessage?: string;
  retryable?: boolean;
  cause?: unknown;
  code?: number;
};

function isErrorLike(error: unknown): error is Error & { code?: number } {
  return typeof error === 'object' && error !== null && 'message' in error;
}

export function normalizeError(error: unknown, options: NormalizeErrorOptions = {}): AppError {
  const baseError = isErrorLike(error) ? error : new Error(options.technicalMessage || 'Unknown error');
  const technicalMessage = options.technicalMessage || baseError.message || 'Unknown error';
  const normalizedError = new Error(options.userMessage || technicalMessage) as AppError;

  normalizedError.name = baseError.name || 'Error';
  normalizedError.stack = baseError.stack;
  normalizedError.code = options.code ?? baseError.code;
  normalizedError.cause = options.cause ?? error;
  normalizedError.source = options.source || 'unknown';
  normalizedError.severity = options.severity || 'error';
  normalizedError.technicalMessage = technicalMessage;
  normalizedError.userMessage = options.userMessage || technicalMessage;
  normalizedError.retryable = options.retryable ?? false;

  return normalizedError;
}

export function reportError(error: unknown, options: NormalizeErrorOptions = {}): AppError {
  const normalizedError = normalizeError(error, options);

  Sentry.withScope(scope => {
    scope.setLevel(normalizedError.severity === 'warning' ? 'warning' : 'error');
    scope.setTag('error.source', normalizedError.source);
    scope.setTag('error.retryable', String(normalizedError.retryable));

    if (normalizedError.code !== undefined) {
      scope.setTag('error.code', String(normalizedError.code));
    }

    scope.setContext('appError', {
      technicalMessage: normalizedError.technicalMessage,
      userMessage: normalizedError.userMessage,
    });

    Sentry.captureException(normalizedError);
  });

  return normalizedError;
}

export async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  const rawBody = await response.text();
  const jsonBody = rawBody ? JSON.parse(rawBody) : null;

  if (!response.ok) {
    const message = jsonBody?.error?.message || response.statusText || 'Request failed';
    throw normalizeError(new Error(message), {
      code: jsonBody?.error?.code,
      technicalMessage: message,
      userMessage: message,
      source: 'network',
      retryable: response.status >= 500,
      cause: jsonBody,
    });
  }

  return jsonBody as T;
}
